import type { WalletFieldFormat, WalletPassKind } from "@lemmonhq/types";

/**
 * The token vocabulary per pass kind.
 *
 * This registry is the single source of truth for the editor's token menu,
 * save-time validation (an unknown token fails), preview sample values, and the
 * runtime binding values each renderer must supply.
 */
export type WalletBindingToken = {
  token: string;
  description: string;
  type: WalletFieldFormat;
  /** Resolved in editor previews and fixture tests. */
  sample: string;
};

const RESTAURANT_NAME: WalletBindingToken = {
  token: "restaurant.name",
  description: "Restaurant or group name",
  type: "text",
  sample: "Café Lemmon",
};

const LOYALTY_QR: WalletBindingToken = {
  token: "loyalty.qr",
  description: "Scannable loyalty account code",
  type: "text",
  sample: "lemmon-loyalty:8f3a21c0",
};

export const WALLET_BINDINGS: Record<WalletPassKind, WalletBindingToken[]> = {
  membership: [
    LOYALTY_QR,
    {
      token: "customer.name",
      description: "Member name",
      type: "text",
      sample: "Camille Rochat",
    },
    {
      token: "customer.short_id",
      description: "Short member ID",
      type: "text",
      sample: "8F3A21C0",
    },
    {
      token: "customer.status_level",
      description: "Membership status",
      type: "text",
      sample: "Active",
    },
    {
      token: "customer.created_at",
      description: "Member since",
      type: "date",
      sample: "2024-03-12T09:30:00.000Z",
    },
    {
      token: "membership.expires_at",
      description: "Membership expiry",
      type: "date",
      sample: "2026-12-31T23:00:00.000Z",
    },
    {
      token: "membership.last_used_at",
      description: "Last time a benefit was used",
      type: "date",
      sample: "2026-09-20T12:15:00.000Z",
    },
    {
      token: "membership_program.name",
      description: "Membership program name",
      type: "text",
      sample: "Friends of Lemmon",
    },
    {
      token: "reward.text",
      description: "Membership benefit",
      type: "text",
      sample: "10% off every order",
    },
    RESTAURANT_NAME,
  ],
  coin: [
    LOYALTY_QR,
    {
      token: "customer.name",
      description: "Customer name",
      type: "text",
      sample: "Camille Rochat",
    },
    {
      token: "customer.short_id",
      description: "Short customer ID",
      type: "text",
      sample: "8F3A21C0",
    },
    {
      token: "coins.balance",
      description: "Coin balance",
      type: "number",
      sample: "5",
    },
    {
      token: "coins.required",
      description: "Coins needed for the next reward",
      type: "number",
      sample: "10",
    },
    {
      token: "coins.label",
      description: "Coin label (e.g. stamp)",
      type: "text",
      sample: "stamp",
    },
    {
      token: "reward.text",
      description: "Next reward",
      type: "text",
      sample: "10 stamps = 1 free coffee",
    },
    {
      token: "coin_program.name",
      description: "Coin program name",
      type: "text",
      sample: "Coffee Club",
    },
    RESTAURANT_NAME,
  ],
  reservation: [
    {
      token: "reservation.party_name",
      description: "Guest name",
      type: "text",
      sample: "Camille Rochat",
    },
    {
      token: "reservation.party_size",
      description: "Party size",
      type: "number",
      sample: "4",
    },
    {
      token: "reservation.time",
      description: "Reservation start",
      type: "date",
      sample: "2026-08-21T17:30:00.000Z",
    },
    {
      token: "reservation.end_time",
      description: "Reservation end",
      type: "date",
      sample: "2026-08-21T19:30:00.000Z",
    },
    {
      token: "reservation.tables",
      description: "Assigned tables",
      type: "text",
      sample: "Table 12",
    },
    {
      token: "reservation.notes",
      description: "Notes",
      type: "text",
      sample: "Window seat requested",
    },
    RESTAURANT_NAME,
    {
      token: "restaurant.address",
      description: "Address",
      type: "text",
      sample: "Rue du Rhône 42, Genève",
    },
    {
      token: "restaurant.phone",
      description: "Phone",
      type: "text",
      sample: "+41 22 555 0134",
    },
  ],
  gift_card: [
    {
      token: "gift_card.code",
      description: "Gift card code",
      type: "text",
      sample: "LMN-4K2P-9XQ7",
    },
    {
      token: "gift_card.balance",
      description: "Current balance",
      type: "currency",
      sample: "CHF 75.00",
    },
    {
      token: "gift_card.initial_balance",
      description: "Initial balance",
      type: "currency",
      sample: "CHF 100.00",
    },
    {
      token: "gift_card.currency",
      description: "Currency",
      type: "text",
      sample: "CHF",
    },
    {
      token: "gift_card.expires_at",
      description: "Expiry date",
      type: "date",
      sample: "2026-12-31T23:00:00.000Z",
    },
    RESTAURANT_NAME,
  ],
};

export type WalletBindingValues = Record<
  string,
  string | number | null | undefined
>;

const TOKEN_PATTERN = /\{\{\s*([\w.]+)\s*\}\}/g;

/** Every token referenced by a template, in order of appearance. */
export const walletTemplateTokens = (template: string): string[] =>
  [...template.matchAll(TOKEN_PATTERN)].map((match) => match[1]);

/** The registry entry for a token, or undefined when the kind lacks it. */
export const walletBindingToken = (
  kind: WalletPassKind,
  token: string,
): WalletBindingToken | undefined =>
  WALLET_BINDINGS[kind].find((entry) => entry.token === token);

/** Sample values for previews and fixture tests. */
export const walletSampleBindings = (
  kind: WalletPassKind,
): WalletBindingValues =>
  Object.fromEntries(
    WALLET_BINDINGS[kind].map((entry) => [entry.token, entry.sample]),
  );

/** Replaces each `{{token}}` with its value; unknown or empty tokens vanish. */
export const resolveWalletTemplate = (
  template: string,
  bindings: WalletBindingValues,
): string =>
  template.replace(TOKEN_PATTERN, (_match, token: string) => {
    const value = bindings[token];
    return value == null ? "" : String(value);
  });
